import { Employee, Job, IconNames, TerminologyOptions } from "@/types/v2";
import {
  Card as BaseCard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import { Badge } from "@/components/Base/Badge";
import { Icon } from "@/components/Common/Icon";
import { Tooltip } from "@/components/Common/Tooltip";
import {
  getEmployeeInitials,
  getEmployeeName,
  getServiceAdvisorColor,
} from "@/lib/employees";
import { getTextColor } from "@/lib/utils";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer.ts";

export interface JobsProps {
  jobs: Job[] | null;
  /** Override the "Jobs" title style (e.g. the RO side panel's compact title). */
  titleClassName?: string;
  /** Used to resolve each job's `technicianId` into a name/initials badge. */
  employees?: Employee[];
  /**
   * The RO's service advisor. One advisor covers every job on the RO, so the
   * badges are colored from this rather than per-job -- matching the card's
   * `CardTechSALabel`, where the background is the advisor's color and the
   * initials are the technician's.
   */
  serviceWriterId?: string | null;
}

export const Jobs = ({
  jobs,
  titleClassName = "text-xl print:text-lg",
  employees = [],
  serviceWriterId,
}: JobsProps) => {
  const { terminolize } = useTerminologyReplacer();
  const badgeColor = getServiceAdvisorColor(serviceWriterId, employees);
  if (!jobs || jobs.length === 0) {
    return (
      <BaseCard className="bg-white print:hidden">
        <CardHeader>
          <CardTitle className={titleClassName}>Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 flex items-center justify-center">
              <Icon
                name={IconNames.jobs}
                size={24}
                className="text-muted-foreground"
              />
            </div>
            <h3 className="mb-2 text-lg font-medium text-muted-foreground">
              No Jobs Yet
            </h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              Jobs added to this{" "}
              {terminolize(
                "repair order",
                TerminologyOptions.repairOrder,
                "singular"
              )}{" "}
              will appear here.
            </p>
          </div>
        </CardContent>
      </BaseCard>
    );
  }

  return (
    <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:bg-white print:p-2">
      <CardHeader>
        <CardTitle className={titleClassName}>Jobs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {jobs.map((job, index) => {
          const estimatedHours = job.laborHours || 0;
          const loggedHours = job.loggedHours || 0;
          const hasCompletedDate = !!job.completedDate;
          const progressPercentage = hasCompletedDate
            ? 100
            : estimatedHours > 0
              ? Math.min((loggedHours / estimatedHours) * 100, 100)
              : 0;
          // An empty string means either no assigned technician or one that
          // is not in the employees list -- both render without a badge.
          const technicianInitials = getEmployeeInitials(
            job.technicianId,
            employees
          );
          const technicianLabel = `${terminolize(
            "Technician",
            TerminologyOptions.technician,
            "singular"
          )}: ${getEmployeeName(job.technicianId, employees)}`;
          // A missing authorizedDate means no decision has been recorded yet
          // -- treat as pending regardless of the raw authorized value. Some
          // jobs synced before the WEBV3-224 backend fix still have
          // authorized: false with no authorizedDate; this keeps them
          // reading correctly until they resync.
          const authorizationStatus =
            job.authorizedDate == null
              ? "pending"
              : job.authorized === true
                ? "authorized"
                : "declined";
          // A finished job gets one green badge, not two: the authorization
          // badge itself reports completion and the percent stays plain text.
          // completedDate wins over authorization and hours (mirrors
          // deriveJobState); the hours-based check is the fallback.
          const isComplete =
            hasCompletedDate ||
            (authorizationStatus === "authorized" &&
              estimatedHours > 0 &&
              progressPercentage === 100);

          return (
            <div
              key={index}
              className="rounded-lg border bg-card bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <div className="flex-1">
                  {job.name && (
                    <h3 className="text-base font-semibold">{job.name}</h3>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {technicianInitials && (
                    <Tooltip message={technicianLabel}>
                      <span
                        aria-label={technicianLabel}
                        tabIndex={0}
                        className="flex h-5 min-w-[20px] items-center justify-center rounded-[4px] border border-white px-1 text-[10px] font-bold leading-none shadow-[inset_1px_1px_1px_#0000001A]"
                        style={{
                          backgroundColor: badgeColor,
                          // getTextColor only parses 6-digit hex; the "#333"
                          // no-advisor fallback yields undefined → white.
                          color: getTextColor(badgeColor) ?? "white",
                        }}
                      >
                        {technicianInitials}
                      </span>
                    </Tooltip>
                  )}
                  <Badge
                    variant={
                      isComplete
                        ? "success"
                        : authorizationStatus === "authorized"
                          ? "default"
                          : authorizationStatus === "declined"
                            ? "destructive"
                            : "secondary"
                    }
                  >
                    {isComplete
                      ? "Complete"
                      : authorizationStatus === "authorized"
                        ? "Authorized"
                        : authorizationStatus === "declined"
                          ? "Declined"
                          : "Pending"}
                  </Badge>
                </div>
              </div>

              {(authorizationStatus !== "declined" || isComplete) &&
                (job.laborHours || job.loggedHours) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex gap-4">
                        {job.laborHours !== undefined && (
                          <div className="flex items-center gap-1">
                            <Icon
                              name={IconNames.clock}
                              size={14}
                              className="text-muted-foreground"
                            />
                            <span className="font-medium">Estimated:</span>
                            <span>{job.laborHours}h</span>
                          </div>
                        )}
                        {job.loggedHours !== undefined && (
                          <div className="flex items-center gap-1">
                            <Icon
                              name={IconNames.check}
                              size={14}
                              className="text-muted-foreground"
                            />
                            <span className="font-medium">Logged:</span>
                            <span>{job.loggedHours}h</span>
                          </div>
                        )}
                      </div>
                      {estimatedHours > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {progressPercentage.toFixed(0)}% Complete
                        </span>
                      )}
                    </div>

                    {estimatedHours > 0 && (
                      <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            progressPercentage === 100
                              ? "bg-success"
                              : "bg-primary"
                          }`}
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
            </div>
          );
        })}
      </CardContent>
    </BaseCard>
  );
};
